import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Suspense, lazy, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Shell } from './app/Shell'
import { ConfirmProvider } from './components/ui/confirm'
import { ToastProvider } from './components/ui/toast'
import { Spinner } from './components/ui/primitives'
import { ApiError } from './lib/http'
import { AuthProvider, useAuth } from './lib/auth'
import { ForgotPage, LoginPage, MfaPage, TokenPage } from './pages/auth/AuthPages'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      retry: (count, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
        return count < 2
      },
    },
  },
})

const Dashboard = lazy(() => import('./pages/Dashboard'))
const Campaigns = lazy(() => import('./pages/Campaigns'))
const NextDay = lazy(() => import('./pages/NextDay'))
const Explore = lazy(() => import('./pages/Explore'))
const Sales = lazy(() => import('./pages/Sales'))
const ClientSales = lazy(() => import('./pages/ClientSales'))
const Submissions = lazy(() => import('./pages/submit/Submissions'))
const SubmissionEditor = lazy(() => import('./pages/submit/SubmissionEditor'))
const Rules = lazy(() => import('./pages/rules/Rules'))
const RuleEditor = lazy(() => import('./pages/rules/RuleEditor'))
const Reports = lazy(() => import('./pages/reports/Reports'))
const Scripts = lazy(() => import('./pages/scripts/Scripts'))
const ScriptDetail = lazy(() => import('./pages/scripts/ScriptDetail'))
const ScriptReview = lazy(() => import('./pages/scripts/ScriptReview'))
const OpeningStudio = lazy(() => import('./pages/OpeningStudio'))
const Creative = lazy(() => import('./pages/Creative'))
const LpImages = lazy(() => import('./pages/LpImages'))
const Hypotheses = lazy(() => import('./pages/Hypotheses'))
const Kashika = lazy(() => import('./pages/Kashika'))
const OcodeAnalytics = lazy(() => import('./pages/OcodeAnalytics'))
const Experiments = lazy(() => import('./pages/Experiments'))
const WorkflowPage = lazy(() => import('./pages/WorkflowPage'))
const EditorSchedule = lazy(() => import('./pages/EditorSchedule'))
const CrowdWorks = lazy(() => import('./pages/CrowdWorks'))
const Skills = lazy(() => import('./pages/Skills'))
const More = lazy(() => import('./pages/More'))
const Account = lazy(() => import('./pages/account/Account'))
const SelectProject = lazy(() => import('./pages/account/SelectProject'))

const Connections = lazy(() => import('./pages/settings/Connections'))
const ConnectionWizard = lazy(() => import('./pages/settings/ConnectionWizard'))
const AdAccounts = lazy(() => import('./pages/settings/AdAccounts'))
const Cases = lazy(() => import('./pages/settings/Cases'))
const Members = lazy(() => import('./pages/settings/Members'))
const DirectoryPage = lazy(() => import('./pages/settings/Directory'))
const Judgement = lazy(() => import('./pages/settings/Judgement'))
const Notifications = lazy(() => import('./pages/settings/Notifications'))
const PlanPage = lazy(() => import('./pages/settings/Plan'))
const AuditPage = lazy(() => import('./pages/settings/Audit'))

const OpsOverview = lazy(() => import('./pages/ops/OpsOverview'))
const OpsProjects = lazy(() => import('./pages/ops/OpsProjects'))
const OpsProjectDetail = lazy(() => import('./pages/ops/OpsProjectDetail'))
const OpsPlans = lazy(() => import('./pages/ops/OpsPlans'))
const OpsUsers = lazy(() => import('./pages/ops/OpsUsers'))
const OpsSettings = lazy(() => import('./pages/ops/OpsSettings'))
const OpsJobs = lazy(() => import('./pages/ops/OpsJobs'))
const OpsAudit = lazy(() => import('./pages/ops/OpsAudit'))

function PageFallback() {
  return (
    <div className="flex h-64 items-center justify-center" role="status" aria-label="読み込み中">
      <Spinner className="h-6 w-6" />
    </div>
  )
}

function FullScreenSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas" role="status" aria-label="読み込み中">
      <Spinner className="h-6 w-6" />
    </div>
  )
}

/** ログイン・二段階認証・契約の選択を済ませた人だけを中へ通す。 */
function Gate() {
  const { me, loading, directoryReady } = useAuth()
  const { pathname } = useLocation()
  if (loading) return <FullScreenSpinner />
  if (!me?.user) return <Navigate to="/login" replace state={{ from: pathname }} />
  if (me.mfa !== 'ok') return <Navigate to="/mfa" replace />
  const free = pathname.startsWith('/ops') || pathname.startsWith('/account') || pathname === '/select-project'
  if (!me.activeProject && !free) return <Navigate to={me.user.isOperator ? '/ops' : '/select-project'} replace />
  if (me.activeProject && !directoryReady) return <FullScreenSpinner />
  return <Shell />
}

function Home() {
  const { me } = useAuth()
  if (me?.activeProject) return <Navigate to="/dashboard" replace />
  return <Navigate to={me?.user?.isOperator ? '/ops' : '/select-project'} replace />
}

/** 運営だけの画面。 */
function OpsOnly({ children }: { children: ReactNode }) {
  const { me } = useAuth()
  if (!me?.user?.isOperator) return <Navigate to="/" replace />
  return <>{children}</>
}

function PublicOnly({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth()
  if (loading) return <FullScreenSpinner />
  if (me?.user && me.mfa === 'ok') return <Navigate to="/" replace />
  return <>{children}</>
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfirmProvider>
          <AuthProvider>
            <BrowserRouter basename="/app">
              <Suspense fallback={<FullScreenSpinner />}>
                <Routes>
                  <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
                  <Route path="/forgot" element={<PublicOnly><ForgotPage /></PublicOnly>} />
                  <Route path="/invite/:token" element={<TokenPage kind="invite" />} />
                  <Route path="/reset/:token" element={<TokenPage kind="reset" />} />
                  <Route path="/mfa" element={<MfaPage />} />
                  <Route path="/mfa-setup" element={<MfaPage voluntary />} />

                  <Route element={<Gate />}>
                    <Route index element={<Home />} />
                    <Route path="/select-project" element={<Suspense fallback={<PageFallback />}><SelectProject /></Suspense>} />
                    <Route path="/account" element={<Account />} />

                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/articles" element={<Dashboard initialTab="articles" />} />
                    <Route path="/campaigns" element={<Campaigns />} />
                    <Route path="/next-day" element={<NextDay />} />
                    <Route path="/explore" element={<Explore />} />
                    <Route path="/sales" element={<Sales />} />
                    <Route path="/cases/:caseId/ads" element={<ClientSales grain="ad-level" />} />
                    <Route path="/cases/:caseId/campaigns" element={<ClientSales grain="campaign-representative" />} />

                    <Route path="/submit" element={<Submissions />} />
                    <Route path="/submit/:id" element={<SubmissionEditor />} />
                    <Route path="/rules" element={<Rules />} />
                    <Route path="/rules/new" element={<RuleEditor />} />
                    <Route path="/rules/:id" element={<RuleEditor />} />
                    <Route path="/auto-control" element={<Navigate to="/rules" replace />} />
                    <Route path="/reports" element={<Reports />} />

                    <Route path="/scripts" element={<Scripts />} />
                    <Route path="/scripts/:id" element={<ScriptDetail />} />
                    <Route path="/scripts/:id/review" element={<ScriptReview />} />
                    <Route path="/opening-studio" element={<OpeningStudio />} />
                    <Route path="/creative" element={<Creative />} />
                    <Route path="/lp-images" element={<LpImages />} />
                    <Route path="/hypotheses" element={<Hypotheses />} />
                    <Route path="/kashika" element={<Kashika />} />
                    <Route path="/analytics/ocode" element={<OcodeAnalytics />} />
                    <Route path="/experiments" element={<Experiments />} />
                    <Route path="/workflow" element={<WorkflowPage />} />
                    <Route path="/schedule" element={<EditorSchedule />} />
                    <Route path="/crowdworks" element={<CrowdWorks />} />
                    <Route path="/skills" element={<Skills />} />
                    <Route path="/more" element={<More />} />

                    <Route path="/settings" element={<Navigate to="/settings/connections" replace />} />
                    <Route path="/settings/connections" element={<Connections />} />
                    <Route path="/settings/connections/:id" element={<ConnectionWizard />} />
                    <Route path="/settings/ad-accounts" element={<AdAccounts />} />
                    <Route path="/settings/cases" element={<Cases />} />
                    <Route path="/settings/unit-prices" element={<Navigate to="/settings/cases" replace />} />
                    <Route path="/settings/members" element={<Members />} />
                    <Route path="/settings/directory" element={<DirectoryPage />} />
                    <Route path="/settings/judgement" element={<Judgement />} />
                    <Route path="/settings/notifications" element={<Notifications />} />
                    <Route path="/settings/plan" element={<PlanPage />} />
                    <Route path="/settings/audit" element={<AuditPage />} />

                    <Route path="/ops" element={<OpsOnly><OpsOverview /></OpsOnly>} />
                    <Route path="/ops/projects" element={<OpsOnly><OpsProjects /></OpsOnly>} />
                    <Route path="/ops/projects/:id" element={<OpsOnly><OpsProjectDetail /></OpsOnly>} />
                    <Route path="/ops/plans" element={<OpsOnly><OpsPlans /></OpsOnly>} />
                    <Route path="/ops/users" element={<OpsOnly><OpsUsers /></OpsOnly>} />
                    <Route path="/ops/settings" element={<OpsOnly><OpsSettings /></OpsOnly>} />
                    <Route path="/ops/jobs" element={<OpsOnly><OpsJobs /></OpsOnly>} />
                    <Route path="/ops/audit" element={<OpsOnly><OpsAudit /></OpsOnly>} />

                    <Route path="*" element={<Home />} />
                  </Route>
                </Routes>
              </Suspense>
            </BrowserRouter>
          </AuthProvider>
        </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
  )
}
